import * as XLSX from "xlsx";
import { VisualizationServicesV2 } from "../Services/VisualizationV2";
import { delay } from "lodash";
import Toast from "../Utils/Toast";
import constants from "../Utils/constants";

export function getFullYear(yearString) {
  // const ignoreString = isAlphabetString(yearString);
  // if (ignoreString) return;
  // // Check if the year is in a two-digit format (e.g., "20"), and if so, convert it to a full year.
  // const currentYear = new Date().getFullYear();
  // return parseInt(currentYear.toString().slice(0, 2) + yearString);

  const ignoreString = isAlphabetString(yearString);
  if (ignoreString) return;

  // Convert the two-digit year to a number
  const yearNumber = parseInt(yearString, 10);

  // Get the current year
  const currentYear = new Date().getFullYear();

  // Determine the full year based on the cutoff year
  const cutoffYear = 30; // For example, if the cutoff year is 30, 30 and below will be interpreted as 2000s, and above 30 will be interpreted as 1900s
  return yearNumber <= cutoffYear ? 2000 + yearNumber : 1900 + yearNumber;
}

export function breakString(str) {
  const quarter = str.substring(0, 1); // Extract the first character for quarter
  const year = str.substring(1); // Extract the remaining characters for year
  return { quarter, year };
}

function isAlphabetString(inputString) {
  return /^[a-zA-Z]+$/.test(inputString);
}

export function parseCssToObj(cssText) {
  const cssObj = {};
  const rules = cssText.split(";");

  rules.forEach((rule) => {
    const [property, value] = rule.split(":");
    if (property && value) {
      cssObj[property.trim()] = value.trim();
    }
  });

  return cssObj;
}

export function generateX_axis(data) {
  let temp = [];
  for (let i in data) {
    temp.push(data[i].data.filter((ie) => ie.qy && ie.value).map((e) => e.qy));
  }

  return [...new Set(temp.flat())].sort((a, b) => {
    // Extract year and quarter
    let [aQuarter, aYear] = a.split("Q").map(Number);
    let [bQuarter, bYear] = b.split("Q").map(Number);

    // Compare years first
    if (aYear !== bYear) {
      return aYear - bYear;
    }

    // If years are the same, compare quarters
    return aQuarter - bQuarter;
  });
}

export const downloadFile = (file) => {
  let fileName = file.split("/").pop();
  VisualizationServicesV2.downloadFile(fileName)
    .then((response) => {
      if (response.status === 200) {
        const blob = new Blob([response?.data], {
          type: response.headers["content-type"],
        });
        if (navigator.userAgent.match("CriOS")) {
          delay(() => {
            let reader = new FileReader();

            reader.onload = function (e) {
              window.location.href = reader.result;
            };
            reader.readAsDataURL(blob);
          }, 500);
        } else {
          const url = window.URL.createObjectURL(blob);
          // const isPublished = this.props.board?.boardDetails?.is_published;
          delay(() => {
            // Create a link element
            const link = document.createElement("a");
            link.href = url;

            // Set the filename (if provided in response headers)

            link.setAttribute("download", fileName);

            // Append the link to the document
            document.body.appendChild(link);

            // Trigger the download
            link.click();

            // Clean up by removing the link and revoking the object URL
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }, 500);
        }
      }
    })
    .catch((error) => {
      Toast(
        error && error.data && error.data.error
          ? error.data.error
          : constants.ERROR.SOMETHING_WENT_WRONG,
        "error"
      );
    });
};

// export function downloadFile(url) {
//   // const newWindow = window.open(url, "_self", "noopener,noreferrer");
//   // if (newWindow) newWindow.opener = null;
// }

export function sortData(data) {
  return data.sort((a, b) => {
    // Extract year and quarter
    let [aQuarter, aYear] = a.qy.split("Q").map(Number);
    let [bQuarter, bYear] = b.qy.split("Q").map(Number);

    // Compare years first
    if (aYear !== bYear) {
      return aYear - bYear;
    }

    // If years are the same, compare quarters
    return aQuarter - bQuarter;
  });
}

export function filterQYDataByRange(
  data,
  startYear,
  startQuarter,
  endYear,
  endQuarter,
  type
) {
  return data.map((dataset) => {
    return {
      ...dataset,
      data: dataset.data.filter((item) => {
        return (
          (item.year > startYear ||
            (item.year === startYear && item[type] >= startQuarter)) &&
          (item.year < endYear ||
            (item.year === endYear && item[type] <= endQuarter))
        );
      }),
    };
  });
}

export function filterYearDataByRange(data, startYear, endYear) {
  return data.map((dataset) => {
    return {
      ...dataset,
      data: dataset.data.filter((item) => {
        return (
          (item.year > startYear || item.year === startYear) &&
          (item.year < endYear || item.year === endYear)
        );
      }),
    };
  });
}

export function downloadExcelFile(filetitle, filename, data, columns) {
  // Create a worksheet from the filtered data
  const ws = XLSX.utils.json_to_sheet(data, {
    header: columns.map((col) => col.accessor),
  });
  // Create a new workbook and append the worksheet
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, filetitle);
  // Generate the XLSX file and trigger the download
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

export const getMappingId = (content, type) => {
  let column = "id";
  switch (type) {
    case "company":
      column = "company_segment_id";
      break;
    case "benchmark":
      column = "benchmark_id";
      break;
    case "market_forecast":
      column = "market_forecast_id";
      break;
    case "customer_study":
      column = "customer_study_id";
      break;
    case "ecosystem":
      column = "ecosystem_id";
      break;
    case "market_landscape":
      column = "market_landscape_id";
      break;
    case "project":
      column = "project_id";
      break;
    case "special_report":
      column = "special_report_id";
      break;
  }
  return content[column];
};

// Helper function to ensure unique names for chart
export const makeUniqueSeriesNames = (series) => {
  const nameCount = {};

  return series.map((item) => {
    const baseName = item.name;

    // Check if name already exists, and increment the counter
    if (nameCount[baseName]) {
      nameCount[baseName]++;
      return {
        ...item,
        name: `${baseName} (${nameCount[baseName]})`, // Make the name unique by appending a number
      };
    } else {
      nameCount[baseName] = 1;
      return item; // If no duplicates, keep the original name
    }
  });
};

// Converts days to hours if input is in days or already in hours
export const normalizeToHours = (value, unit) => {
  let expTime = parseInt(value);
  if (unit === "In Days") {
    expTime = expTime * 24;
  }
  return expTime;
};

// // Converts total hours into a readable format like "1d 3h" or "5h"
export const formatHoursToReadableTime = (hours) => {
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;

    let result = `${days}d`;
    if (remainingHours > 0) {
      result += ` ${remainingHours}h`;
    }

    return result;
  }
  return `${hours}h`;
};

export const getFirstLetters = (str) => {
  return str
    ?.split(" ") // Split the string into an array of words
    .map((word) => word.charAt(0)) // Extract the first letter of each word
    .join(""); // Join the letters into a single string
};

export const loggedByMap = {
  MAGIC_LINK: "Magic Link",
  EMAIL_PASSWORD: "EMAIL",
  OIDC_MICROSOFT: "Microsoft",
  OIDC_GOOGLE: "Google",
};
